import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import { CATEGORIES, calcsInCategory } from '../data/calculators'
import { useLocale, localizedHref } from '../i18n/locale'
import { calcName, categoryName } from '../i18n/calculators'

interface IndexContent {
  seoTitle: string
  seoDesc: string
  crumbHome: string
  crumbCalcs: string
  h1: string
  intro: string
  comingSoon: string
  notFoundH2: string
  notFoundLead: string
  requestCta: string
}

const EN: IndexContent = {
  seoTitle: 'All Home Renovation Calculators | RenoSheets',
  seoDesc:
    'Browse all free home improvement calculators: paint, tile, concrete, mulch, flooring, drywall, deck, fence, roofing, insulation and more. Get exact quantities plus a complete buy list.',
  crumbHome: 'Home',
  crumbCalcs: 'Calculators',
  h1: 'All Calculators',
  intro:
    'Every calculator gives you exact quantities plus a complete materials list — including the consumables most people forget. Free, no signup, works in any browser.',
  comingSoon: 'Coming soon',
  notFoundH2: "Don't see what you need?",
  notFoundLead: "New calculators are added regularly. If there's one you need, let us know.",
  requestCta: 'Request a calculator →',
}

const FR: IndexContent = {
  seoTitle: 'Tous les calculateurs de rénovation résidentielle | RenoSheets',
  seoDesc:
    'Parcourez tous les calculateurs de rénovation gratuits : peinture, carrelage, béton, paillis, plancher, gypse, terrasse, clôture, toiture, isolation et plus. Obtenez les quantités exactes plus une liste d’achats complète.',
  crumbHome: 'Accueil',
  crumbCalcs: 'Calculateurs',
  h1: 'Tous les calculateurs',
  intro:
    'Chaque calculateur vous donne les quantités exactes plus une liste complète de matériaux — y compris les consommables que la plupart des gens oublient. Gratuit, sans inscription, fonctionne dans tout navigateur.',
  comingSoon: 'À venir',
  notFoundH2: 'Vous ne trouvez pas ce qu’il vous faut?',
  notFoundLead: 'De nouveaux calculateurs sont ajoutés régulièrement. S’il y en a un qu’il vous faut, dites-le-nous.',
  requestCta: 'Demander un calculateur →',
}

export default function CalculatorsIndex() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Seo
        title={c.seoTitle}
        description={c.seoDesc}
        path="/calculators/"
      />
      <Breadcrumbs items={[{ name: c.crumbHome, href: L('/') }, { name: c.crumbCalcs, href: L('/calculators/') }]} />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-2 text-text-muted max-w-2xl">{c.intro}</p>

      <div className="mt-8 space-y-10">
        {CATEGORIES.map((cat) => {
          const calcs = calcsInCategory(cat.slug)
          const liveCalcs = calcs.filter((calc) => calc.live)
          const comingCalcs = calcs.filter((calc) => !calc.live)
          return (
            <section key={cat.slug}>
              <div className="mb-4 border-b border-border pb-2">
                <h2 className="text-2xl text-text">{categoryName(cat.slug, cat.name, locale)}</h2>
                <p className="text-sm text-text-muted">{cat.blurb}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {liveCalcs.map((calc) => (
                  <Link
                    key={calc.slug}
                    to={L(`/calculators/${calc.slug}/`)}
                    className="group flex items-start gap-4 rounded-xl border border-border bg-surface p-5 transition-shadow hover:shadow-sm hover:border-brand"
                  >
                    {/* Logo-green icon swatch. text-green-800 = #166534 — the
                        exact "Sheets" wordmark green in the header logo. */}
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 text-xl text-green-800 dark:bg-green-500/15 dark:text-green-300">
                      {calc.icon}
                    </span>
                    <div>
                      <div className="font-semibold text-text group-hover:text-brand-text">{calcName(calc.slug, calc.name, locale)}</div>
                      <div className="mt-0.5 text-sm text-text-muted">{calc.tagline}</div>
                    </div>
                  </Link>
                ))}
                {comingCalcs.map((calc) => (
                  <div
                    key={calc.slug}
                    className="flex items-start gap-4 rounded-xl border border-dashed border-border bg-surface-2 p-5 opacity-60"
                  >
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-surface text-xl text-text-subtle">
                      {calc.icon}
                    </span>
                    <div>
                      <div className="font-medium text-text-muted">{calcName(calc.slug, calc.name, locale)}</div>
                      <div className="mt-0.5 text-xs text-text-subtle">{c.comingSoon}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )
        })}
      </div>

      <div className="mt-12 rounded-2xl border border-border bg-brand-tint/30 p-7">
        <h2 className="text-lg font-semibold text-text">{c.notFoundH2}</h2>
        <p className="mt-1 text-text-muted">{c.notFoundLead}</p>
        <a href="mailto:info@renosheets.com" className="mt-3 inline-block text-sm text-brand-text hover:underline">
          {c.requestCta}
        </a>
      </div>
    </div>
  )
}
